import { Button } from '@ads/components-react';
import { AcademicCapIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

export interface ClassModuleEmptyStateProps {
  /** Dispara a criação de um novo módulo (ex.: navegação para `/class-modules/create`). */
  readonly onCreate: () => void;
}

/**
 * Estado vazio da listagem de módulos, exibido quando não há nenhum módulo
 * cadastrado (sem filtros ativos). Reproduz o mockup do design: um selo circular
 * com ícone, título, descrição e a ação primária de criar o primeiro módulo.
 *
 * O container externo (surface/borda/arredondamento) é provido pelo slot de
 * tabela do `ClassModuleListTemplate`; aqui só entra o conteúdo centralizado.
 *
 * @example
 * ```tsx
 * <ClassModuleEmptyState onCreate={() => navigate({ to: '/class-modules/create' })} />
 * ```
 */
export function ClassModuleEmptyState({ onCreate }: ClassModuleEmptyStateProps) {
  const { t } = useTranslation('classModuleManagement');

  return (
    <div className="flex flex-col items-center justify-center gap-xs px-md py-xxl text-center">
      <div className="flex flex-col items-center gap-mini">
        <div className="flex items-center justify-center rounded-circular bg-ctx-interactive-secondary p-xxs">
          <AcademicCapIcon className="size-sm text-ctx-content-brand" />
        </div>

        <div className="flex flex-col items-center gap-nano">
          <h2 className="ts-heading-sm text-ctx-content-title">{t('list.empty.title')}</h2>
          <p className="ts-paragraph-xs text-ctx-content-base max-w-md">
            {t('list.empty.description')}
          </p>
        </div>

        <Button hierarchy="primary" size="md" leadingIcon={PlusIcon} onClick={onCreate}>
          {t('list.empty.createButton')}
        </Button>
      </div>
    </div>
  );
}
